"use client"

import * as React from "react"
import { useVirtualizer } from "@tanstack/react-virtual"
import { cn } from '../../../lib/utils'
import {
  gridViewVariants,
  gridContainerVariants,
  DEFAULT_GAP,
  DEFAULT_ESTIMATE_HEIGHT,
  DEFAULT_OVERSCAN,
  DEFAULT_LOAD_MORE_THRESHOLD,
  DEFAULT_LOADING_ITEM_COUNT,
  DEFAULT_MIN_COLUMN_WIDTH,
  getGapInPixels,
} from "./constants"
import {
  GridViewContext,
  useContainerWidth,
  useResponsiveColumns,
  useScrollInfiniteScroll,
  useMeasuredHeights,
} from "./use-grid-view"
import type { GridViewProps, GridItemData, GridViewContextValue } from "./types"

// ============================================================================
// GridView Component
// ============================================================================

function GridViewInner<TData extends GridItemData>(
  {
    className,
    gap = DEFAULT_GAP,
    items,
    getItemKey,
    columns,
    minColumnWidth = DEFAULT_MIN_COLUMN_WIDTH,
    enableInfiniteScroll = false,
    onLoadMore,
    hasNextPage = false,
    isFetchingNextPage = false,
    loadMoreThreshold = DEFAULT_LOAD_MORE_THRESHOLD,
    estimateItemHeight = DEFAULT_ESTIMATE_HEIGHT,
    overscan = DEFAULT_OVERSCAN,
    isLoading = false,
    loadingItemCount = DEFAULT_LOADING_ITEM_COUNT,
    maxHeight,
    renderItem,
    children,
    ...props
  }: GridViewProps<TData>,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const scrollContainerRef = React.useRef<HTMLDivElement>(null)

  // Container width observation
  const containerWidth = useContainerWidth(containerRef)

  // Calculate responsive column count
  const columnCount = useResponsiveColumns(
    containerWidth,
    columns,
    minColumnWidth,
    gap
  )

  // Height measurement cache
  const { heights: measuredHeights, reportHeight, clearHeights } = useMeasuredHeights()

  // Default key extractor
  const keyExtractor = React.useCallback(
    (item: TData, index: number): string => {
      if (getItemKey) return getItemKey(item, index)
      return item.id
    },
    [getItemKey]
  )

  // Calculate row count
  const rowCount = React.useMemo(() => {
    return Math.ceil(items.length / columnCount)
  }, [items.length, columnCount])

  // Get items for a specific row
  const getRowItems = React.useCallback(
    (rowIndex: number): TData[] => {
      const startIndex = rowIndex * columnCount
      return items.slice(startIndex, startIndex + columnCount)
    },
    [items, columnCount]
  )

  // Estimate row height based on measured heights or default
  const estimateRowHeight = React.useCallback(
    (rowIndex: number): number => {
      const rowItems = getRowItems(rowIndex)
      if (rowItems.length === 0) return 0

      let maxHeight = 0
      for (let i = 0; i < rowItems.length; i++) {
        const item = rowItems[i]
        const key = keyExtractor(item, rowIndex * columnCount + i)
        const measured = measuredHeights.get(key)
        const estimate =
          typeof estimateItemHeight === "function"
            ? estimateItemHeight(rowIndex * columnCount + i)
            : estimateItemHeight
        maxHeight = Math.max(maxHeight, measured ?? estimate)
      }

      return maxHeight + getGapInPixels(gap)
    },
    [getRowItems, keyExtractor, columnCount, measuredHeights, estimateItemHeight, gap]
  )

  // Row virtualizer
  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: estimateRowHeight,
    overscan,
  })

  // Infinite scroll detection
  useScrollInfiniteScroll(scrollContainerRef, {
    enabled: enableInfiniteScroll,
    hasNextPage,
    isFetchingNextPage,
    threshold: loadMoreThreshold,
    onLoadMore,
  })

  // Scroll to index method
  const scrollToIndex = React.useCallback(
    (index: number) => {
      const rowIndex = Math.floor(index / columnCount)
      virtualizer.scrollToIndex(rowIndex, { align: "start" })
    },
    [virtualizer, columnCount]
  )

  // Remeasure method
  const remeasure = React.useCallback(() => {
    clearHeights()
    virtualizer.measure()
  }, [clearHeights, virtualizer])

  // Context value
  const contextValue = React.useMemo<GridViewContextValue<TData>>(
    () => ({
      items,
      columnCount,
      gap,
      isLoading,
      isFetchingNextPage,
      hasNextPage,
      enableInfiniteScroll,
      virtualizer,
      measuredHeights,
      getItemKey: keyExtractor,
      scrollToIndex,
      remeasure,
      reportHeight,
    }),
    [
      items,
      columnCount,
      gap,
      isLoading,
      isFetchingNextPage,
      hasNextPage,
      enableInfiniteScroll,
      virtualizer,
      measuredHeights,
      keyExtractor,
      scrollToIndex,
      remeasure,
      reportHeight,
    ]
  )

  // Separate children by displayName
  const { loader, empty, skeleton, gridItems } = React.useMemo(() => {
    const result = {
      loader: null as React.ReactNode,
      empty: null as React.ReactNode,
      skeleton: null as React.ReactNode,
      gridItems: [] as React.ReactNode[],
    }

    React.Children.forEach(children, (child) => {
      if (!React.isValidElement(child)) return

      const displayName = (child.type as { displayName?: string })?.displayName || ""

      switch (displayName) {
        case "GridLoader":
          result.loader = child
          break
        case "GridEmpty":
          result.empty = child
          break
        case "GridSkeleton":
          result.skeleton = child
          break
        case "GridItem":
          result.gridItems.push(child)
          break
        default:
          // Non-grid children are treated as grid items
          result.gridItems.push(child)
      }
    })

    return result
  }, [children])

  // Render loading state
  if (isLoading) {
    const skeletonCount = loadingItemCount

    return (
      <GridViewContext.Provider value={contextValue as unknown as GridViewContextValue}>
        <div
          ref={ref}
          className={cn(gridViewVariants({ gap }), className)}
          {...props}
        >
          <div ref={containerRef} className="w-full">
            <div
              className={cn(gridContainerVariants({ gap }))}
              style={{
                gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
              }}
            >
              {skeleton ||
                Array.from({ length: skeletonCount }).map((_, i) => (
                  <div
                    key={`skeleton-${i}`}
                    className="animate-pulse rounded-lg bg-muted aspect-square"
                  />
                ))}
            </div>
          </div>
        </div>
      </GridViewContext.Provider>
    )
  }

  // Render empty state
  if (items.length === 0) {
    return (
      <GridViewContext.Provider value={contextValue as unknown as GridViewContextValue}>
        <div
          ref={ref}
          className={cn(gridViewVariants({ gap }), className)}
          {...props}
        >
          <div ref={containerRef} className="w-full">
            {empty || (
              <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground">
                <p>No items to display</p>
              </div>
            )}
          </div>
        </div>
      </GridViewContext.Provider>
    )
  }

  // Get virtual rows
  const virtualRows = virtualizer.getVirtualItems()

  // Calculate total height
  const totalHeight = virtualizer.getTotalSize()

  return (
    <GridViewContext.Provider value={contextValue as unknown as GridViewContextValue}>
      <div
        ref={ref}
        className={cn(gridViewVariants({ gap }), className)}
        {...props}
      >
        <div ref={containerRef} className="w-full flex-1 min-h-0">
          <div
            ref={scrollContainerRef}
            className="overflow-auto"
            style={{
              maxHeight: maxHeight ?? undefined,
              height: maxHeight ? undefined : "100%",
            }}
          >
            {/* Virtual container */}
            <div
              className="relative w-full"
              style={{ height: totalHeight }}
            >
              {virtualRows.map((virtualRow) => {
                const rowItems = getRowItems(virtualRow.index)

                return (
                  <div
                    key={virtualRow.key}
                    className="absolute left-0 right-0"
                    style={{
                      top: virtualRow.start,
                      height: virtualRow.size,
                    }}
                  >
                    <div
                      className={cn(gridContainerVariants({ gap }))}
                      style={{
                        gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
                      }}
                    >
                      {rowItems.map((item, colIndex) => {
                        const itemIndex = virtualRow.index * columnCount + colIndex
                        const key = keyExtractor(item, itemIndex)

                        if (renderItem) {
                          return (
                            <React.Fragment key={key}>
                              {renderItem(item, itemIndex)}
                            </React.Fragment>
                          )
                        }

                        // Find matching GridItem child
                        const matchingChild = gridItems.find((child) => {
                          if (!React.isValidElement(child)) return false
                          return (child.props as { item?: TData })?.item?.id === item.id
                        })

                        if (matchingChild) {
                          return (
                            <React.Fragment key={key}>
                              {matchingChild}
                            </React.Fragment>
                          )
                        }

                        // Default render
                        return (
                          <div
                            key={key}
                            className="rounded-lg bg-sunken p-4"
                          >
                            <p className="text-sm font-medium">{item.id}</p>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Infinite scroll loader */}
            {enableInfiniteScroll && isFetchingNextPage && (
              <div className="py-4">
                {loader || (
                  <div className="flex items-center justify-center gap-2 text-muted-foreground">
                    {/* Ring drawn with SVG strokes, not `border-2` + a
                        transparent top edge (§5) — same 20px spinner. */}
                    <svg
                      aria-hidden="true"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="size-5 animate-spin text-accent"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeOpacity="0.25"
                        strokeWidth="2.5"
                      />
                      <path
                        d="M22 12a10 10 0 0 0-10-10"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                      />
                    </svg>
                    <span className="text-sm">Loading more...</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </GridViewContext.Provider>
  )
}

export const GridView = React.forwardRef(GridViewInner) as <
  TData extends GridItemData
>(
  props: GridViewProps<TData> & { ref?: React.ForwardedRef<HTMLDivElement> }
) => React.ReactElement

;(GridView as React.FC).displayName = "GridView"
